import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, AlertTriangleIcon, AlertCircleIcon, XCircleIcon, ChevronRightIcon, MailIcon, CalendarIcon, FileTextIcon, BookOpenIcon } from 'lucide-react';
import { ServiceNowSubNav } from '../components/ServiceNowSubNav';
type Answer = string | null;
interface Answers {
  q1: Answer;
  q2: Answer;
  q3: Answer;
  q4: Answer;
  q5: Answer;
  q6: Answer;
  q7: Answer;
  q8: Answer;
}
interface Option {
  label: string;
  value: string;
  /** Risk value 0–3 for this answer */
  points: number;
  /** When true, this answer removes the question from scoring entirely */
  excludeFromScoring?: boolean;
}
interface Question {
  id: keyof Answers;
  question: string;
  subtitle: string;
  /** Weight multiplier applied to this question's risk value */
  weight: number;
  options: Option[];
}
const questions: Question[] = [{
  id: 'q1',
  question: 'How many custom tables have you created?',
  subtitle: '(not out-of-box ServiceNow tables)',
  weight: 1,
  options: [{
    label: '0–10 (Low complexity)',
    value: 'a',
    points: 0
  }, {
    label: '11–30 (Moderate complexity)',
    value: 'b',
    points: 1
  }, {
    label: '31–75 (High complexity)',
    value: 'c',
    points: 2
  }, {
    label: '76+ (Critical complexity)',
    value: 'd',
    points: 3
  }]
}, {
  id: 'q2',
  question: 'How do you manage platform upgrades?',
  subtitle: '',
  weight: 1,
  options: [{
    label: 'Formal semi-annual upgrade cadence with a documented testing plan',
    value: 'a',
    points: 0
  }, {
    label: 'We upgrade opportunistically, but no fixed cadence',
    value: 'b',
    points: 1
  }, {
    label: 'We upgrade only when forced (end-of-life, vendor pressure)',
    value: 'c',
    points: 2
  }, {
    label: 'No formal upgrade process — ad hoc or unsure',
    value: 'd',
    points: 3
  }]
}, {
  id: 'q3',
  question: "What's your latest Instance Scan score?",
  subtitle: '(if you don\'t know, select "Don\'t know")',
  weight: 1,
  options: [{
    label: '85–100 (Healthy)',
    value: 'a',
    points: 0
  }, {
    label: '70–84 (Moderate risk)',
    value: 'b',
    points: 1
  }, {
    label: '50–69 (High risk)',
    value: 'c',
    points: 2
  }, {
    label: 'Below 50 (Critical risk)',
    value: 'd',
    points: 3
  }, {
    label: "Don't know / Never run Instance Scan",
    value: 'e',
    points: 2
  }]
}, {
  id: 'q4',
  question: 'How many point-to-point integrations do you have?',
  subtitle: '',
  weight: 1,
  options: [{
    label: '0–5',
    value: 'a',
    points: 0
  }, {
    label: '6–15',
    value: 'b',
    points: 1
  }, {
    label: '16–30',
    value: 'c',
    points: 2
  }, {
    label: '31+',
    value: 'd',
    points: 3
  }]
}, {
  id: 'q5',
  question: 'Do you have formal architecture review before development starts?',
  subtitle: '',
  weight: 1,
  options: [{
    label: 'Yes, all changes reviewed by a certified architect',
    value: 'a',
    points: 0
  }, {
    label: 'Sometimes, for major changes only',
    value: 'b',
    points: 1
  }, {
    label: 'No formal review process',
    value: 'c',
    points: 3
  }, {
    label: "What's architecture review?",
    value: 'd',
    points: 2
  }]
}, {
  id: 'q6',
  question: 'Do you have a designated source of truth for core data domains (identity, assets, etc.)?',
  subtitle: '',
  weight: 1.5,
  options: [{
    label: 'Yes, documented and enforced for all core data domains',
    value: 'a',
    points: 0
  }, {
    label: "Partially — some domains have a designated source, others don't",
    value: 'b',
    points: 1
  }, {
    label: 'No designated source; discrepancies exist between systems',
    value: 'c',
    points: 3
  }, {
    label: 'Unsure / never checked',
    value: 'd',
    points: 2
  }]
}, {
  id: 'q7',
  question: 'Do you have a dedicated Platform Owner with real cross-module authority?',
  subtitle: '',
  weight: 1.5,
  options: [{
    label: 'Dedicated Platform Owner role with real cross-module authority',
    value: 'a',
    points: 0
  }, {
    label: 'Informal ownership, split across multiple people/roles',
    value: 'b',
    points: 1
  }, {
    label: 'No clear owner — decisions made ad hoc',
    value: 'c',
    points: 3
  }, {
    label: 'Role is vacant or was recently vacated',
    value: 'd',
    points: 3
  }]
}, {
  id: 'q8',
  question: 'Does your platform require an ATO?',
  subtitle: '(DoD/IC Bonus Question)',
  weight: 2,
  options: [{
    label: "Yes, and it's current",
    value: 'a',
    points: 0
  }, {
    label: "Yes, and it's expired or at risk",
    value: 'b',
    points: 3
  }, {
    label: 'No ATO required',
    value: 'c',
    points: 0,
    excludeFromScoring: true
  }, {
    label: "Don't know",
    value: 'd',
    points: 2
  }]
}];
function getOption(qId: keyof Answers, value: string): Option | undefined {
  const q = questions.find(q => q.id === qId);
  if (!q) return undefined;
  return q.options.find(o => o.value === value);
}
function calculateScore(answers: Answers): number {
  let numerator = 0;
  let denominator = 0;
  for (const q of questions) {
    const val = answers[q.id];
    if (!val) continue;
    const opt = getOption(q.id, val);
    if (!opt) continue;
    // "No ATO required" removes the question from both numerator and denominator
    if (opt.excludeFromScoring) continue;
    numerator += opt.points * q.weight;
    denominator += 3 * q.weight;
  }
  if (denominator === 0) return 0;
  return Math.round(numerator / denominator * 100);
}
function getRiskLevel(score: number): {
  level: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: React.ReactNode;
  description: string;
} {
  if (score <= 25) return {
    level: 'Low Risk',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    icon: <CheckCircleIcon className="h-8 w-8 text-green-500" data-id="element-761" data-mp="28kh1em12rhcp" />,
    description: 'Your platform is relatively healthy with minimal technical debt and strong governance fundamentals.'
  };
  if (score <= 50) return {
    level: 'Moderate Risk',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    icon: <AlertCircleIcon className="h-8 w-8 text-yellow-500" data-id="element-762" data-mp="1px88nrpdekpg" />,
    description: 'Manageable today, but growing governance gaps will create real friction if left unaddressed.'
  };
  if (score <= 75) return {
    level: 'High Risk',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    icon: <AlertTriangleIcon className="h-8 w-8 text-orange-500" data-id="element-763" data-mp="28u9bb5vgc8q8" />,
    description: 'Significant technical debt and governance gaps are likely blocking upgrades and elevating compliance risk.'
  };
  return {
    level: 'Critical Risk',
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    icon: <XCircleIcon className="h-8 w-8 text-red-500" data-id="element-764" data-mp="35cxims3sh38x" />,
    description: 'Severe technical debt and missing governance put upgrades, security, and ATO at urgent risk.'
  };
}
function getRecommendations(answers: Answers): string[] {
  const recs: string[] = [];
  const {
    q1,
    q2,
    q3,
    q4,
    q5,
    q6,
    q7,
    q8
  } = answers;
  // ATO risk is the most urgent, surface it first
  if (q8 === 'b') {
    recs.push('An expired or at-risk ATO is an urgent issue. Every undocumented change increases re-authorization risk. Establish ATO governance and document all platform changes immediately.');
  }
  if (q7 === 'c' || q7 === 'd') {
    recs.push('Without a clear Platform Owner holding real cross-module authority, decisions default to whoever is loudest. Establish a dedicated (or fractional) Platform Owner role to restore accountability and prevent ungoverned sprawl.');
  }
  if (q6 === 'c' || q6 === 'd') {
    recs.push('No designated source of truth for core data domains guarantees drift and reconciliation firefighting. Stand up a data governance framework that names an authoritative system for each domain and enforces deference to it.');
  }
  if (q1 === 'c' || q1 === 'd') {
    recs.push('Your high number of custom tables suggests heavy customization is blocking upgrades. Start with a technical debt audit and upgrade roadmap to understand the scope of remediation needed.');
  }
  if (q2 === 'c' || q2 === 'd') {
    recs.push('Upgrading only when forced (or with no formal process) lets technical debt compound between releases. Establish a semi-annual upgrade cadence with a documented testing plan as an early governance win.');
  }
  if (q3 === 'e') {
    recs.push('No Instance Scan history means you have zero visibility into your technical health. Run Instance Scan immediately and review findings—this is the fastest way to quantify your technical debt.');
  } else if (q3 === 'c' || q3 === 'd') {
    recs.push('Your low Instance Scan score indicates significant technical debt. Focus on remediating high-severity findings before they compound into upgrade blockers or security vulnerabilities.');
  }
  if (q5 === 'c' || q5 === 'd') {
    recs.push('Lack of architecture review is allowing new technical debt to accumulate with every development sprint. Implement a governance framework immediately to stop the bleeding before addressing existing debt.');
  }
  if (q4 === 'c' || q4 === 'd') {
    recs.push('Your integration sprawl (16+ integrations) without governance standards creates fragility. A data governance framework with integration patterns will reduce failure risk and maintenance burden.');
  }
  if (recs.length === 0) {
    recs.push('Your platform shows relatively low technical debt. Focus on maintaining architecture standards and establishing a governance framework to prevent future accumulation.');
    recs.push('Consider a Fractional Platform Owner engagement to provide ongoing governance oversight and quarterly maturity assessments.');
    recs.push('Run a proactive application rationalization assessment to ensure your ServiceNow portfolio is optimized for maximum ROI.');
  }
  return recs.slice(0, 3);
}
export function CalculatorPage() {
  const [answers, setAnswers] = useState<Answers>({
    q1: null,
    q2: null,
    q3: null,
    q4: null,
    q5: null,
    q6: null,
    q7: null,
    q8: null
  });
  const [showResults, setShowResults] = useState(false);
  const [email, setEmail] = useState('');
  const [sendReport, setSendReport] = useState(false);
  const [subscribe, setSubscribe] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const totalQuestions = questions.length;
  const answeredCount = Object.values(answers).filter(Boolean).length;
  const allAnswered = answeredCount === totalQuestions;
  const score = showResults ? calculateScore(answers) : 0;
  const risk = showResults ? getRiskLevel(score) : null;
  const recommendations = showResults ? getRecommendations(answers) : [];
  const handleCalculate = () => {
    if (allAnswered) setShowResults(true);
  };
  const handleReset = () => {
    setAnswers({
      q1: null,
      q2: null,
      q3: null,
      q4: null,
      q5: null,
      q6: null,
      q7: null,
      q8: null
    });
    setShowResults(false);
    setEmail('');
    setSendReport(false);
    setSubscribe(false);
    setSubmitted(false);
  };
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  return <div className="w-full" data-id="element-765" data-mp="53j2athejwse">
      <ServiceNowSubNav data-id="element-766" data-mp="56f1sm8wetiy" />
      {/* HERO */}
      <section className="section-navy hero-grid-pattern" data-id="element-767" data-mp="a3oy8lupg5ga">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-768" data-mp="a12fdq2d63ir">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-769" data-mp="3rdd7tacvvgwm">
            <div className="inline-block px-3 py-1 bg-nrs-orange/20 border border-nrs-orange/40 rounded-full text-nrs-orange text-xs font-semibold uppercase tracking-wider mb-4" data-id="element-770" data-mp="1pkytsmdgmwlh">
              Free Self-Assessment Tool
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight max-w-4xl mx-auto" data-id="element-771" data-mp="1o308s8mkmelq">
              Is Your ServiceNow Platform at Risk?
            </h1>
            <div className="h-1.5 w-24 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-772" data-mp="1j471ixxvnju0"></div>
            <p className="text-xl text-nrs-light font-bold mb-4 max-w-2xl mx-auto" data-id="element-773" data-mp="1f3wm6uo73h2h">
              Answer 8 quick questions to get your Technical Debt Risk Score and
              personalized recommendations.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-light-blue min-h-screen" data-id="element-774" data-mp="221zxfga32thw">
        <div className="max-w-2xl mx-auto" data-id="element-775" data-mp="1y166ee1cgyks">
          <AnimatePresence mode="wait" data-id="element-776" data-mp="1y04wwdzz8k56">
            {!showResults ? <motion.div key="questions" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-777" data-mp="2uj2hcn89frc5">
                {/* Progress Bar */}
                <div className="mb-8" data-id="element-778" data-mp="ca9873f6bcdy">
                  <div className="flex items-center justify-between mb-2" data-id="element-779" data-mp="cbanjk382l1h">
                    <span className="text-sm font-semibold text-gray-600" data-id="element-780" data-mp="h9u9vllq9yud">
                      {answeredCount} of {totalQuestions} questions answered
                    </span>
                    <span className="text-sm text-gray-400" data-id="element-781" data-mp="qa0jgweb2eo9">
                      {Math.round(answeredCount / totalQuestions * 100)}%
                      complete
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-782" data-mp="106vjjjq88n4n">
                    <motion.div className="h-full bg-nrs-orange rounded-full" animate={{
                  width: `${answeredCount / totalQuestions * 100}%`
                }} transition={{
                  duration: 0.3
                }} data-id="element-783" data-mp="14xvcyf050n2e" />
                  </div>
                </div>

                {/* Questions */}
                <div className="space-y-6" data-id="element-784" data-mp="1iz7qkjrtscl4">
                  {questions.map((q, qi) => <motion.div key={q.id} initial={{
                opacity: 0,
                y: 16
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: qi * 0.05
              }} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-785" data-mp="15b62d9hye9cn" data-mpi={qi}>
                      <div className="px-6 py-4 border-b border-gray-100 bg-nrs-light" data-id="element-786" data-mp="1a3h1490yjp3m">
                        <div className="flex items-start gap-3" data-id="element-787" data-mp="mdmpfusdk6ky">
                          <span className="flex-shrink-0 w-7 h-7 bg-nrs-blue rounded-full flex items-center justify-center text-white text-xs font-bold" data-id="element-788" data-mp="hdqk9s7xy93t">
                            {qi + 1}
                          </span>
                          <div data-id="element-789" data-mp="rbeqbeazz31a">
                            <p className="font-heading font-semibold text-nrs-navy text-sm md:text-base" data-id="element-790" data-mp="ra3fwvn1qx17">
                              {q.question}
                            </p>
                            {q.subtitle && <p className="text-gray-500 text-xs mt-0.5" data-id="element-791" data-mp="22qbgirix3ya8">
                                {q.subtitle}
                              </p>}
                          </div>
                        </div>
                      </div>
                      <div className="px-6 py-4 space-y-2" data-id="element-792" data-mp="219o0ui14gw3p">
                        {q.options.map(opt => {
                    const isSelected = answers[q.id] === opt.value;
                    return <button key={opt.value} onClick={() => setAnswers(prev => ({
                      ...prev,
                      [q.id]: opt.value
                    }))} className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-all ${isSelected ? 'border-nrs-blue bg-nrs-light text-nrs-navy font-semibold' : 'border-gray-200 bg-white text-gray-700 hover:border-nrs-blue/50 hover:bg-gray-50'}`} data-id="element-793" data-mp="25dc9l41hpxqj">
                              <div className="flex items-center gap-3" data-id="element-794" data-mp="3bzy6vyd387gf">
                                <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${isSelected ? 'border-nrs-blue bg-nrs-blue' : 'border-gray-300'}`} data-id="element-795" data-mp="3bxvamwlcdi39">
                                  {isSelected && <div className="w-full h-full rounded-full bg-white scale-50" data-id="element-796" data-mp="3qllw3xy5e265" />}
                                </div>
                                {opt.label}
                              </div>
                            </button>;
                  })}
                      </div>
                    </motion.div>)}
                </div>

                {/* Calculate Button */}
                <div className="mt-8" data-id="element-797" data-mp="37p3tumloajvn">
                  <button onClick={handleCalculate} disabled={!allAnswered} className={`w-full py-4 rounded-xl font-heading font-bold text-lg transition-all ${allAnswered ? 'bg-nrs-orange text-white hover:bg-amber-500 shadow-lg' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-798" data-mp="32wubpsqsuo3x">
                    {allAnswered ? 'Calculate My Risk Score →' : `Answer all questions to continue (${totalQuestions - answeredCount} remaining)`}
                  </button>
                </div>
              </motion.div> : <motion.div key="results" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-799" data-mp="2k0axip152a0i">
                {/* Score Display */}
                <div className={`rounded-2xl border-2 ${risk!.borderColor} ${risk!.bgColor} p-8 text-center mb-6`} data-id="element-800" data-mp="2jsqsw94xzt4y">
                  <div className="flex justify-center mb-4" data-id="element-801" data-mp="2op7rxydojrwt">{risk!.icon}</div>
                  <div className="text-gray-500 text-sm mb-1" data-id="element-802" data-mp="1r4v0vhqbipei">
                    Your Technical Debt Risk Score
                  </div>
                  <div className="font-heading font-extrabold text-6xl text-nrs-navy mb-2" data-id="element-803" data-mp="1w0acs44rmb8a">
                    {score}
                  </div>
                  <div className="text-gray-400 text-sm mb-3" data-id="element-804" data-mp="25thn83ed4hf8">out of 100</div>
                  <div className={`inline-block px-4 py-1.5 rounded-full font-heading font-bold text-sm ${risk!.color} ${risk!.bgColor} border ${risk!.borderColor}`} data-id="element-805" data-mp="18dk96ycrq8lk">
                    {risk!.level}
                  </div>
                  <p className={`mt-4 text-sm leading-relaxed ${risk!.color} max-w-lg mx-auto`} data-id="element-806" data-mp="2rjn504sbzv47">
                    {risk!.description}
                  </p>
                </div>

                {/* Recommendations */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6" data-id="element-807" data-mp="31i2u627ssub2">
                  <h2 className="font-heading font-bold text-lg text-nrs-navy mb-4" data-id="element-808" data-mp="2wgwwrr370iv7">
                    Your Personalized Recommendations
                  </h2>
                  <div className="space-y-4" data-id="element-809" data-mp="36ge4ee2096tt">
                    {recommendations.map((rec, i) => <div key={i} className="flex gap-3" data-id="element-810" data-mp="3k79fzy3w2213" data-mpi={i}>
                        <div className="flex-shrink-0 w-6 h-6 bg-nrs-orange rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5" data-id="element-811" data-mp="3k6rbyfazijim">
                          {i + 1}
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed" data-id="element-812" data-mp="3u3ce8svmdgir">
                          {rec}
                        </p>
                      </div>)}
                  </div>
                </div>

                {/* Email Capture */}
                {!submitted ? <div className="bg-nrs-light rounded-xl border border-nrs-blue/20 p-6 mb-6" data-id="element-813" data-mp="gn1x4h322kq3">
                    <h3 className="font-heading font-bold text-base text-nrs-navy mb-1" data-id="element-814" data-mp="2nis3x5jjpi0">
                      Get Your Detailed Report
                    </h3>
                    <p className="text-gray-600 text-sm mb-4" data-id="element-815" data-mp="3p4euooqvab9z">
                      Enter your email to receive a detailed breakdown of your
                      risk score and remediation roadmap.
                    </p>
                    <form onSubmit={handleEmailSubmit} className="space-y-3" data-id="element-816" data-mp="3g49nofpl5ewc">
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your.email@agency.gov" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nrs-blue" required data-id="element-817" data-mp="3l2bg3r8wiqkh" />
                      <div className="space-y-2" data-id="element-818" data-mp="3b6httg5dripi">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-id="element-819" data-mp="3a8qedso6mb5k">
                          <input type="checkbox" checked={sendReport} onChange={e => setSendReport(e.target.checked)} className="rounded" data-id="element-820" data-mp="3f94d2k3w88qx" />
                          Send me my detailed risk score report
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-id="element-821" data-mp="35bzpqgyba78j">
                          <input type="checkbox" checked={subscribe} onChange={e => setSubscribe(e.target.checked)} className="rounded" data-id="element-822" data-mp="3t1kkdx3tusxn" />
                          Subscribe to ServiceNow Governance Insights newsletter
                        </label>
                      </div>
                      <button type="submit" className="w-full py-2.5 bg-nrs-blue text-white font-semibold text-sm rounded-lg hover:bg-blue-800 transition-colors" data-id="element-823" data-mp="3o76ntrdfifre">
                        Send My Report
                      </button>
                    </form>
                  </div> : <div className="bg-green-50 rounded-xl border border-green-200 p-6 mb-6 text-center" data-id="element-824" data-mp="259bg181ir8ea">
                    <CheckCircleIcon className="h-8 w-8 text-green-500 mx-auto mb-2" data-id="element-825" data-mp="20brz20dfshin" />
                    <p className="font-semibold text-green-800" data-id="element-826" data-mp="2f0ef64aw1dax">
                      Report sent! Check your inbox shortly.
                    </p>
                  </div>}

                {/* Next Steps */}
                <div className="card mb-6" data-id="element-827" data-mp="1mafns2981433">
                  <h2 className="text-xl font-bold mb-4" data-id="element-828" data-mp="1mdbjrl1idiow">Next Steps</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-829" data-mp="1hdfqafqft9nl">
                    <a href="mailto:info@newriversystems.com" className="border border-nrs-blue/20 rounded-lg p-4 hover:border-nrs-blue hover:shadow-sm transition-all text-center" data-id="element-830" data-mp="1vc5oxzswnhe2">
                      <CalendarIcon className="h-6 w-6 text-nrs-blue mx-auto mb-2" data-id="element-831" data-mp="36qsnll04txf7" />
                      <div className="font-bold text-sm text-nrs-navy mb-1" data-id="element-832" data-mp="36swzap49w0q1">
                        Free Consultation
                      </div>
                      <p className="text-gray-500 text-xs" data-id="element-833" data-mp="1zdi9b56yibqd">
                        Discuss your results with a ServiceNow governance expert
                      </p>
                    </a>
                    <a href="#" className="border border-nrs-blue/20 rounded-lg p-4 hover:border-nrs-blue hover:shadow-sm transition-all text-center" data-id="element-834" data-mp="29cheuewfdr95">
                      <FileTextIcon className="h-6 w-6 text-nrs-blue mx-auto mb-2" data-id="element-835" data-mp="2dc02vjjjmig7" />
                      <div className="font-bold text-sm text-nrs-navy mb-1" data-id="element-836" data-mp="1gpwq7m7c3t9a">
                        Download Report
                      </div>
                      <p className="text-gray-500 text-xs" data-id="element-837" data-mp="1po8fnkwgsl05">
                        "The Federal ServiceNow Technical Debt Audit Guide"
                        (PDF)
                      </p>
                    </a>
                    <Link to="/services/servicenow/case-studies" className="border border-nrs-blue/20 rounded-lg p-4 hover:border-nrs-blue hover:shadow-sm transition-all text-center" data-id="element-838" data-mp="xydm1c1kq95o">
                      <BookOpenIcon className="h-6 w-6 text-nrs-blue mx-auto mb-2" data-id="element-839" data-mp="ug6fe9m714nc" />
                      <div className="font-heading font-semibold text-sm text-nrs-navy mb-1" data-id="element-840" data-mp="pgtisx5isj7k">
                        Read Case Study
                      </div>
                      <p className="text-gray-500 text-xs" data-id="element-841" data-mp="ld5cehsmjtnw">
                        How an agency reduced technical debt from Critical to
                        Low in 6 months
                      </p>
                    </Link>
                  </div>
                </div>

                <button onClick={handleReset} className="w-full py-3 border-2 border-gray-300 text-gray-600 font-semibold rounded-xl hover:border-nrs-blue hover:text-nrs-blue transition-colors" data-id="element-842" data-mp="6pahcur59d7n">
                  Retake Assessment
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>
      </section>
    </div>;
}